import React, { useState, useEffect } from 'react';
import { Show, Venue } from '../../types';
import { api } from '../../services/api';

interface ShowFormProps {
  show?: Show | null;
  onSubmit: (data: any) => void;
  onClose: () => void;
}

// Generate time options for dropdowns with groupings
const generateTimeOptions = () => {
  const times = [];

  // Morning header and times (8 AM - 11:45 AM)
  times.push({ value: '--morning--', label: '—— Morning ——', disabled: true });
  for (let hour = 8; hour <= 11; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const time = `${hour}:${minute.toString().padStart(2, '0')} AM`;
      times.push({ value: time, label: time, disabled: false });
    }
  }

  // Afternoon header and times (12 PM - 4:45 PM)
  times.push({ value: '--afternoon--', label: '—— Afternoon ——', disabled: true });
  for (let minute = 0; minute < 60; minute += 15) {
    const time = `12:${minute.toString().padStart(2, '0')} PM`;
    times.push({ value: time, label: time, disabled: false });
  }
  for (let hour = 1; hour <= 4; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const time = `${hour}:${minute.toString().padStart(2, '0')} PM`;
      times.push({ value: time, label: time, disabled: false });
    }
  }

  // Evening header and times (5 PM - 8:45 PM)
  times.push({ value: '--evening--', label: '—— Evening ——', disabled: true });
  for (let hour = 5; hour <= 8; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const time = `${hour}:${minute.toString().padStart(2, '0')} PM`;
      times.push({ value: time, label: time, disabled: false });
    }
  }

  // Night header and times (9 PM - 11:45 PM)
  times.push({ value: '--night--', label: '—— Night ——', disabled: true });
  for (let hour = 9; hour <= 11; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const time = `${hour}:${minute.toString().padStart(2, '0')} PM`;
      times.push({ value: time, label: time, disabled: false });
    }
  }

  // Late night (12:00 AM - 2:00 AM)
  for (let minute = 0; minute < 60; minute += 15) {
    const time = `12:${minute.toString().padStart(2, '0')} AM`;
    times.push({ value: time, label: time, disabled: false });
  }

  // 1 AM - 2 AM
  for (let hour = 1; hour <= 2; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const time = `${hour}:${minute.toString().padStart(2, '0')} AM`;
      times.push({ value: time, label: time, disabled: false });
    }
  }

  return times;
};

const TIME_OPTIONS = generateTimeOptions();

// Duration options
const DURATION_OPTIONS = [
  '30 minutes',
  '1 hour',
  '1.5 hours',
  '2 hours',
  '2.5 hours',
  '3 hours',
  '3.5 hours',
  '4 hours',
  '5 hours',
  '6 hours',
  'All day'
];

const ShowForm: React.FC<ShowFormProps> = ({ show, onSubmit, onClose }) => {
  // Helper function to format date for input field (local date)
  const formatDateForInput = (dateInput: string | Date | undefined) => {
    if (!dateInput) return '';

    // If it's an ISO string like "2025-10-04T00:00:00.000Z"
    if (typeof dateInput === 'string' && dateInput.includes('T')) {
      // Extract just the date part (YYYY-MM-DD)
      return dateInput.split('T')[0];
    }

    // If it's a Date object
    if (dateInput instanceof Date) {
      const year = dateInput.getFullYear();
      const month = String(dateInput.getMonth() + 1).padStart(2, '0');
      const day = String(dateInput.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    // If it's already in YYYY-MM-DD format
    return dateInput;
  };

  const [formData, setFormData] = useState({
    date: show ? formatDateForInput(show.date) : '',
    venue: show?.venue || {
      id: '',
      name: '',
      address: {
        street: '',
        city: 'Austin',
        state: 'TX',
        zip: ''
      },
      contacts: []
    },
    startTime: show?.startTime || '7:00 PM',
    duration: show?.duration || '3 hours',
    eventType: show?.eventType || (show?.isPrivate ? 'private' : show?.isTicketed ? 'ticketed' : 'public'),
    bandConfig: show?.bandConfig || 'full',
    streamUrl: show?.streamUrl || '',
    notes: show?.notes || '',
    isCancelled: show?.isCancelled || false,
    ticketUrl: show?.ticketUrl || '',
    flyerUrl: show?.flyerUrl || ''
  });

  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(show?.flyerUrl || null);
  const [showVenueSuggestions, setShowVenueSuggestions] = useState(false);
  const [venues, setVenues] = useState<Venue[]>([]);
  const [loadingVenues, setLoadingVenues] = useState(false);

  // Check if we're using venue default image
  const [useVenueDefault, setUseVenueDefault] = useState(
    !!(show?.venue?.defaultImageUrl && show?.flyerUrl === show?.venue?.defaultImageUrl) ||
    (!show && false) // For new shows, we'll set this when venue is selected
  );

  // Fetch venues from database on component mount
  useEffect(() => {
    const fetchVenues = async () => {
      setLoadingVenues(true);
      try {
        const venuesData = await api.getVenues();
        setVenues(venuesData);
      } catch (error) {
        console.error('Error fetching venues:', error);
      } finally {
        setLoadingVenues(false);
      }
    };
    fetchVenues();
  }, []);

  // Close venue suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.venue-input-container')) {
        setShowVenueSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      return;
    }

    setUploadingImage(true);

    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64 = event.target?.result as string;

        try {
          const data = await api.uploadImage(base64);
          setFormData({ ...formData, flyerUrl: data.url });
          setImagePreview(data.url);
        } catch (err) {
          alert('Failed to upload image');
        } finally {
          setUploadingImage(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image');
      setUploadingImage(false);
    }
  };

  const handleVenueNameChange = (name: string) => {
    // Generate venue ID from name (lowercase, replace spaces with hyphens)
    const id = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

    setFormData({
      ...formData,
      venue: {
        ...formData.venue,
        id: id,
        name: name
      }
    });
  };

  const selectVenue = (venue: Venue) => {
    // If venue has a default image, use it by default for new shows
    const shouldUseDefault = !!(venue.defaultImageUrl && !show);

    setFormData({
      ...formData,
      venue: venue,
      // Use venue's default image if checkbox will be checked
      flyerUrl: shouldUseDefault && venue.defaultImageUrl ? venue.defaultImageUrl : formData.flyerUrl
    });

    // Set checkbox state
    if (venue.defaultImageUrl) {
      setUseVenueDefault(shouldUseDefault);
      // Update image preview
      if (shouldUseDefault) {
        setImagePreview(venue.defaultImageUrl);
      }
    }

    setShowVenueSuggestions(false);
  };

  const handleDefaultImageToggle = (checked: boolean) => {
    setUseVenueDefault(checked);
    if (checked && formData.venue.defaultImageUrl) {
      // Use venue's default image
      setFormData({ ...formData, flyerUrl: formData.venue.defaultImageUrl });
      setImagePreview(formData.venue.defaultImageUrl);
    } else {
      // Clear the image to allow custom upload
      setFormData({ ...formData, flyerUrl: '' });
      setImagePreview(null);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Ensure venue has an ID
    if (!formData.venue.id && formData.venue.name) {
      formData.venue.id = formData.venue.name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    }

    // Calculate end time from duration if needed
    let endTime = '';
    if (formData.duration && formData.duration !== 'All day') {
      // This is a simplified calculation - in production you'd want more robust time math
      const durationHours = parseFloat(formData.duration.split(' ')[0]);
      // For now, just pass the duration
    }

    // Keep the date as YYYY-MM-DD string and append time to make it noon local time
    // This avoids timezone conversion issues
    const dateString = `${formData.date}T12:00:00.000Z`;

    onSubmit({
      ...formData,
      date: dateString,
      endTime: endTime || undefined
    });
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium">
            {show ? 'Edit Show' : 'Add New Show'}
          </h3>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date
            </label>
            <input
              type="date"
              required
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Venue */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {formData.eventType === 'internet' ? 'Platform / Show Name' : 'Venue'}
            </label>
            <div className="relative venue-input-container">
              <input
                type="text"
                required
                placeholder={formData.eventType === 'internet' ? 'e.g., Facebook Live, YouTube, Instagram' : 'Enter venue name'}
                value={formData.venue.name}
                onChange={(e) => handleVenueNameChange(e.target.value)}
                onFocus={() => setShowVenueSuggestions(true)}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
              {showVenueSuggestions && formData.eventType !== 'internet' && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
                  {loadingVenues ? (
                    <div className="p-3 text-sm text-gray-500">Loading venues...</div>
                  ) : venues.length > 0 ? (
                    <>
                      <div className="p-2 text-xs text-gray-500">Select from saved venues:</div>
                      {venues
                        .filter(venue =>
                          venue.name.toLowerCase().includes(formData.venue.name.toLowerCase()) ||
                          formData.venue.name.length === 0
                        )
                        .map(venue => (
                          <button
                            key={venue.id}
                            type="button"
                            onClick={() => selectVenue(venue)}
                            className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm"
                          >
                            <div className="font-medium">{venue.name}</div>
                            {venue.address.street && (
                              <div className="text-xs text-gray-500">
                                {venue.address.street}, {venue.address.city}, {venue.address.state}
                              </div>
                            )}
                          </button>
                        ))}
                      {venues.filter(venue =>
                        venue.name.toLowerCase().includes(formData.venue.name.toLowerCase()) ||
                        formData.venue.name.length === 0
                      ).length === 0 && (
                        <div className="p-3 text-sm text-gray-500">
                          No matching venues. Enter a new venue name to create one.
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="p-3 text-sm text-gray-500">
                      No venues saved yet. Enter a venue name to create a new one.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Address Fields or Stream URL based on event type */}
          {formData.eventType === 'internet' ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Stream URL
              </label>
              <input
                type="text"
                placeholder="e.g., www.youtube.com/@uplateaustin or https://facebook.com/events/..."
                value={formData.streamUrl}
                onChange={(e) => setFormData({ ...formData, streamUrl: e.target.value })}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter the URL where viewers can watch the internet show (with or without https://)
              </p>
            </div>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Street Address
                </label>
                <input
                  type="text"
                  placeholder="123 Main St"
                  value={formData.venue.address.street}
                  onChange={(e) => setFormData({
                    ...formData,
                    venue: {
                      ...formData.venue,
                      address: { ...formData.venue.address, street: e.target.value }
                    }
                  })}
                  className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    City
                  </label>
                  <input
                    type="text"
                    placeholder="Austin"
                    value={formData.venue.address.city}
                    onChange={(e) => setFormData({
                      ...formData,
                      venue: {
                        ...formData.venue,
                        address: { ...formData.venue.address, city: e.target.value }
                      }
                    })}
                    className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    State
                  </label>
                  <input
                    type="text"
                    placeholder="TX"
                    value={formData.venue.address.state}
                    onChange={(e) => setFormData({
                      ...formData,
                      venue: {
                        ...formData.venue,
                        address: { ...formData.venue.address, state: e.target.value }
                      }
                    })}
                    className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    maxLength={2}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ZIP
                  </label>
                  <input
                    type="text"
                    placeholder="78701"
                    value={formData.venue.address.zip}
                    onChange={(e) => setFormData({
                      ...formData,
                      venue: {
                        ...formData.venue,
                        address: { ...formData.venue.address, zip: e.target.value }
                      }
                    })}
                    className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </>
          )}

          {/* Time and Duration */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Time
              </label>
              <select
                required
                value={formData.startTime}
                onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select time</option>
                {TIME_OPTIONS.map(timeOption => (
                  <option
                    key={`start-${timeOption.value}`}
                    value={timeOption.disabled ? '' : timeOption.value}
                    disabled={timeOption.disabled}
                    className={timeOption.disabled ? 'font-semibold text-gray-500' : ''}
                  >
                    {timeOption.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Duration
              </label>
              <select
                value={formData.duration}
                onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select duration</option>
                {DURATION_OPTIONS.map(duration => (
                  <option key={duration} value={duration}>
                    {duration}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Event Type and Band Config */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Event Type
              </label>
              <select
                value={formData.eventType}
                onChange={(e) => setFormData({ ...formData, eventType: e.target.value as 'private' | 'internet' | 'ticketed' | 'public' })}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="public">Public</option>
                <option value="private">Private</option>
                <option value="ticketed">Ticketed</option>
                <option value="internet">Internet</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Band Configuration
              </label>
              <select
                value={formData.bandConfig}
                onChange={(e) => setFormData({ ...formData, bandConfig: e.target.value as 'full' | 'duet' | 'trio' })}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="full">Full Band</option>
                <option value="duet">Duet</option>
                <option value="trio">Trio</option>
              </select>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              rows={3}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              placeholder="Special instructions, opening acts, etc."
            />
          </div>

          {/* Image Upload Section */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Event Image
            </label>
            <div className="space-y-3">
              {/* Default Venue Image Checkbox */}
              {formData.venue.defaultImageUrl && (
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={useVenueDefault}
                    onChange={(e) => handleDefaultImageToggle(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Default Venue Image?</span>
                </label>
              )}

              {/* Image preview */}
              {imagePreview && (
                <div className="relative">
                  <img
                    src={imagePreview}
                    alt="Event flyer preview"
                    className="w-full h-48 object-cover rounded-md"
                  />
                  {!useVenueDefault && (
                    <button
                      type="button"
                      onClick={() => {
                        setFormData({ ...formData, flyerUrl: '' });
                        setImagePreview(null);
                      }}
                      className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              )}

              {/* Upload button - disabled when using venue default */}
              <div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={uploadingImage || useVenueDefault}
                  className={`w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold ${
                    useVenueDefault
                      ? 'file:bg-gray-100 file:text-gray-400 cursor-not-allowed'
                      : 'file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100'
                  }`}
                />
                {uploadingImage && (
                  <p className="text-sm text-gray-500">Uploading image...</p>
                )}
                {!formData.venue.defaultImageUrl && (
                  <p className="text-xs text-gray-500 mt-1">
                    Upload an event flyer or poster. Max size: 5MB.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Cancelled checkbox and Ticket URL for ticketed events */}
          <div className="space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.isCancelled}
                onChange={(e) => setFormData({ ...formData, isCancelled: e.target.checked })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Cancelled</span>
            </label>

            {/* Ticket URL field - shown when event type is ticketed */}
            {formData.eventType === 'ticketed' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ticket Purchase URL
                </label>
                <input
                  type="text"
                  placeholder="https://ticketvendor.com/event..."
                  value={formData.ticketUrl}
                  onChange={(e) => setFormData({ ...formData, ticketUrl: e.target.value })}
                  className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">Link to ticket purchase page</p>
              </div>
            )}
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              {show ? 'Update' : 'Create'} Show
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ShowForm;
